import { Component, OnDestroy } from '@angular/core';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { PERMITTED_VIEWS, USER_TYPE } from 'src/app/core/helpers/global/global.constant';
import { BUTTON_ACTIONS } from 'src/app/core/helpers/ui/ui.constant';
import { PlayerModalFormComponent } from 'src/app/features/player/components/modals/player-modal-form/player-modal-form.component';
import {
  QUICK_ACCESS_MOBILE_BREAKPOINT_PX,
  QUICK_ACCESS_NAV_MODE_BOTTOM_BAR,
  QUICK_ACCESS_NAV_MODE_HIDDEN,
} from 'src/app/core/helpers/ui/quick-access.constant';
import {
  QuickAccessFloatingSide,
  QuickAccessNavMode,
  QuickAccessPage,
} from 'src/app/core/interfaces/ui/quick-access.interface';
import { BootstrapModalService } from 'src/app/core/services/ui/bootstrap-modal.service';
import { GlobalService } from 'src/app/core/services/ui/global.service';
import { QuickAccessService } from 'src/app/core/services/ui/quick-access.service';
import { TransferUserModalComponent } from '../header/elements/transfer-user-modal/transfer-user-modal.component';

const LONG_PRESS_NAVIGATION_GUARD_MS = 400;

@Component({
  selector: 'shared-mobile-navbar',
  templateUrl: './mobile-navbar.component.html',
})
export class MobileNavbarComponent implements OnDestroy {
  public navMode: QuickAccessNavMode = QUICK_ACCESS_NAV_MODE_BOTTOM_BAR;
  public readonly bottomBarMode = QUICK_ACCESS_NAV_MODE_BOTTOM_BAR;
  public readonly hiddenMode = QUICK_ACCESS_NAV_MODE_HIDDEN;

  public floatingPositionY: number | null = null;
  public floatingSide: QuickAccessFloatingSide = 'right';

  public suppressNavigation: boolean = false;
  public showCustomizerHint: boolean = false;
  private hintShownThisSession: boolean = false;

  private userId: string = '';

  private permittedPages: QuickAccessPage[] = [];
  public quickAccessSlots: (QuickAccessPage | null)[] = [];

  private hintTimer?: ReturnType<typeof setTimeout>;
  private onDestroy$: Subject<boolean> = new Subject();

  constructor(
    private modalService: BootstrapModalService<any>,
    private globalService: GlobalService,
    private quickAccessService: QuickAccessService
  ) {
    combineLatest([this.globalService.profile, this.quickAccessService.getPermittedPages$()])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([profile, permittedPages]) => {
        this.userId = profile?._id ?? '';
        this.permittedPages = permittedPages;
        this.refreshQuickAccessItems();
      });

    this.quickAccessService.selectionChanged$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.refreshQuickAccessItems());
  }

  ngOnDestroy() {
    clearTimeout(this.hintTimer);
    this.onDestroy$.next(true);
  }

  public openTransferModal() {
    this.modalService.openModal({
      component: TransferUserModalComponent,
      data: USER_TYPE.PLAYER,
    });
  }

  private openAddPlayerModal(): void {
    this.modalService.openModal({
      component: PlayerModalFormComponent,
      data: { buttonAction: BUTTON_ACTIONS.ADD },
    });
  }

  public onQuickAccessLongPress(): void {
    this.suppressNavigation = true;
    this.dismissCustomizerHint();
    this.quickAccessService.openCustomizer();
    setTimeout(() => (this.suppressNavigation = false), LONG_PRESS_NAVIGATION_GUARD_MS);
  }

  public dismissCustomizerHint(): void {
    clearTimeout(this.hintTimer);
    this.showCustomizerHint = false;
  }

  public onActionSlotClick(slot: QuickAccessPage): void {
    if (this.suppressNavigation || !slot.isAction) return;

    switch (slot.id) {
      case PERMITTED_VIEWS.BTN_TRANSFER_PLAYER:
        this.openTransferModal();
        return;
      case PERMITTED_VIEWS.BTN_ADD_PLAYER:
        this.openAddPlayerModal();
        return;
    }
  }

  public onFloatingPositionChanged(y: number): void {
    if (!this.userId) return;
    this.floatingPositionY = y;
    this.quickAccessService.saveFloatingPositionY(this.userId, y);
  }

  public onFloatingSideChanged(side: QuickAccessFloatingSide): void {
    if (!this.userId) return;
    this.floatingSide = side;
    this.quickAccessService.saveFloatingSide(this.userId, side);
  }

  private refreshQuickAccessItems(): void {
    this.quickAccessSlots = this.userId
      ? this.quickAccessService.resolveSelection(this.userId, this.permittedPages)
      : [];

    this.navMode = this.userId
      ? this.quickAccessService.getNavMode(this.userId)
      : QUICK_ACCESS_NAV_MODE_BOTTOM_BAR;
    this.floatingPositionY = this.userId
      ? this.quickAccessService.getFloatingPositionY(this.userId)
      : null;
    this.floatingSide = this.userId
      ? this.quickAccessService.getFloatingSide(this.userId)
      : 'right';

    this.maybeShowCustomizerHint();
  }

  private maybeShowCustomizerHint(): void {
    const widgetIsVisible =
      window.innerWidth < QUICK_ACCESS_MOBILE_BREAKPOINT_PX && this.navMode !== this.hiddenMode;

    if (
      !widgetIsVisible ||
      this.hintShownThisSession ||
      !this.userId ||
      !this.quickAccessSlots.some((slot) => slot !== null) ||
      this.quickAccessService.hasSeenCustomizerHint(this.userId)
    ) {
      return;
    }

    this.hintShownThisSession = true;
    this.quickAccessService.markCustomizerHintSeen(this.userId);
    this.showCustomizerHint = true;
    this.hintTimer = setTimeout(() => (this.showCustomizerHint = false), 12000);
  }
}
